import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui";
import { 
  Users, 
  Calendar, 
  Database,
  UserCog 
} from 'lucide-react';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { logout, hasPermission } = useAuth();
  const location = useLocation();

  const getLinkClassName = (path: string) => {
    const isActive = location.pathname === path;
    return isActive
      ? "border-indigo-500 text-gray-900 inline-flex items-center px-1 pt-1 border-b-2"
      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 inline-flex items-center px-1 pt-1 border-b-2";
  };

  // Base navigation items always visible
  const baseNavItems: NavItem[] = [
    { label: '林员列表', path: '/', icon: <Users className="h-5 w-5" /> },
    { label: '年度管理', path: '/years', icon: <Calendar className="h-5 w-5" /> },
  ];
  
  // Permission-based navigation items
  const permissionNavItems: NavItem[] = [];
  
  // Only show user management to users with user_manage permission
  if (hasPermission('user_manage')) {
    permissionNavItems.push({ 
      label: '用户管理', 
      path: '/admin/users', 
      icon: <UserCog className="h-5 w-5" />
    });
  }
  
  // Only show database management to users with database_manage permission
  if (hasPermission('database_manage')) {
    permissionNavItems.push({ 
      label: '数据库管理', 
      path: '/admin/database', 
      icon: <Database className="h-5 w-5" /> 
    });
  }
  
  // Combine both arrays
  const navItems: NavItem[] = [...baseNavItems, ...permissionNavItems];

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <Link to="/" className="flex-shrink-0 flex items-center">
                居士林林员管理系统
              </Link>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={getLinkClassName(item.path)}
                  >
                    <span className="flex items-center">
                      {item.icon}
                      <span className="ml-2">{item.label}</span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
            <div className="flex items-center">
              <Button
                onClick={logout}
                variant="ghost"
                className="text-gray-500 hover:text-gray-700"
              >
                退出登录
              </Button>
            </div>
          </div>
        </div>
      </nav>
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">{children}</main>
    </div>
  );
};

export default Layout;
